import React, { FC } from 'react';
import { Button } from '@duxton-web/ui';
import { Popover, Tooltip } from 'antd';
import { debounce } from 'lodash';

import { Text } from '../../../common/components';
import ConstraintsViewer, {
  ConstraintsViewerType,
  ConstraintsViewerType_Keys,
} from '../../../common/components/ConstraintsViewer/ConstraintsViewer';
import { DefaultValueIcon, RolloutIcon } from '../../../common/Icons';
import { searchByConstraintsEvents } from '../../../common/Scribe/EventsList';
import Service, { Constraint, RolloutTarget } from '../../../lib/App';
import { sendScribeEvent } from '../../../utils/scribe';
import {
  FacetType,
  generateScribeEventData,
  VariableSearchColumn,
} from './VariablesSearch';
import { controlTreatmentRenderer } from './VariablesSearchExperiment';
import { TypeIDWrapper, ValueWrapper } from './VariablesSearchRecords';
import { FACETS } from '@/constants/facets';
import { appendEnvParam } from '@/utils/navigation';
import { getUrlBasedOffOriginAndDomainFamily } from '@/utils/url';

interface OwnProps {
  variable: any;
  matchingRolloutTarget: Map<number, RolloutTarget>;
  colIndex: number;
  columns: VariableSearchColumn[];
  renderAll?: boolean;
  searchVal?: string;
  filters?: { [key: string]: any };
  pageNumber?: number;
  total?: number;
}

const GLOBAL_FACETS = [FACETS.Country, FACETS.City, FACETS.VehicleTypeID];

type VariablesSearchRolloutProps = OwnProps;

export const matchConstraint = (constraint, facet, value: any): boolean => {
  if (!constraint?.value || !constraint?.target || !value || !facet)
    return true;

  if (constraint.target !== facet) return false;

  let found = constraint.target === facet && constraint.value === value;

  if (found) return found;

  const pattern = new RegExp(`[\\[,]${value}[\\],]`);
  return constraint.target === facet && constraint.value.match(pattern);
};

export const filterByCityVehicleTypeIDs = (
  constraint: Constraint,
  columns: VariableSearchColumn[],
  colIndex: number,
) => {
  const cityID = columns?.[colIndex].constraint.value;
  const vehicleTypeIDs = Service.cache.getVehiclesByCityID(
    parseInt(cityID, 10),
  );

  if (!vehicleTypeIDs?.length) return false;

  return vehicleTypeIDs.some((vehicleTypeID) =>
    matchConstraint(constraint, FacetType.VECHILE_TYPE_ID, vehicleTypeID),
  );
};

export const filterByCountryCities = (
  constraint: Constraint,
  columns: VariableSearchColumn[],
  colIndex: number,
) => {
  const countryCode = columns?.[colIndex].constraint.value;
  const cities = Service.cache.getCitiesByCountryCode(countryCode);

  if (!cities?.length) return false;

  return cities.some((cityID) =>
    matchConstraint(constraint, FacetType.CITY, cityID),
  );
};

export const filterByCountryVehicleTypeIDs = (
  constraint: Constraint,
  columns: VariableSearchColumn[],
  colIndex: number,
) => {
  const countryCode = columns?.[colIndex].constraint.value;
  const vehicleTypeIDs = Service.cache.getVehiclesByCountryCode(countryCode);

  if (!vehicleTypeIDs?.length) return false;

  return vehicleTypeIDs.some((vehicleTypeID) =>
    matchConstraint(constraint, FacetType.VECHILE_TYPE_ID, vehicleTypeID),
  );
};

export const hasMatchingConstraints = (
  constraints: Constraint[],
  columns: VariableSearchColumn[],
  colIndex: number,
): boolean => {
  if (!constraints?.length) return true;

  let isGlobalRolloutTarget = true;

  const facet = columns?.[colIndex].constraint.facet;
  const value = columns?.[colIndex].constraint.value;

  return (
    constraints.filter((constraint: Constraint, index: number) => {
      const { target } = constraint;

      if (GLOBAL_FACETS.includes(target)) isGlobalRolloutTarget = false;

      if (index === constraints.length - 1 && isGlobalRolloutTarget)
        return isGlobalRolloutTarget;

      let found = matchConstraint(constraint, facet, value);

      if (found) return found;

      if (facet === FacetType.CITY) {
        return filterByCityVehicleTypeIDs(constraint, columns, colIndex);
      }

      if (facet === FacetType.COUNTRY) {
        found = filterByCountryCities(constraint, columns, colIndex);

        if (found) return found;

        return filterByCountryVehicleTypeIDs(constraint, columns, colIndex);
      }

      return found;
    }).length > 0
  );
};

export const filterRolloutTargetMatchingFacet = (
  rolloutTargets: RolloutTarget[],
  columns: VariableSearchColumn[],
  colIndex: number,
  availableSlots: number = 0,
): Map<number, RolloutTarget> => {
  const matchingRolloutTargets = new Map<number, RolloutTarget>();

  if (!rolloutTargets?.length) return matchingRolloutTargets;

  if (availableSlots === 0) return matchingRolloutTargets;

  for (let index = 0; index < rolloutTargets.length; index += 1) {
    if (
      Array.from(matchingRolloutTargets.keys())?.length >= availableSlots &&
      availableSlots > -1
    )
      break;

    const rolloutTarget = rolloutTargets[index];

    if (hasMatchingConstraints(rolloutTarget.constraints, columns, colIndex)) {
      matchingRolloutTargets.set(index + 1, rolloutTarget);
    }
  }

  return matchingRolloutTargets;
};

export const renderConstraintPopover = (
  variable,
  constraintParent,
  index: number,
  type: (typeof ConstraintsViewerType)[ConstraintsViewerType_Keys],
) => {
  let items = [];
  let href = getUrlBasedOffOriginAndDomainFamily(
    `/experiments/${constraintParent.name}/overview`,
  );

  if (type === ConstraintsViewerType.EXPERIMENT)
    items = controlTreatmentRenderer(variable, constraintParent, index, true);

  if (type === ConstraintsViewerType.ROLLOUT) {
    href = getUrlBasedOffOriginAndDomainFamily(
      `/variables/${variable.name}/rollout?highlightTargetId=${index}`,
    );
  }

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 800,
        width: 'fit-content',
        maxHeight: 800,
        overflow: 'hidden auto',
        gap: '8px',
      }}
    >
      <ConstraintsViewer
        rollout={variable?.rollouts}
        rolloutTarget={constraintParent}
        rolloutTargetToCompare={constraintParent}
        rolloutToCompare={variable?.rollouts}
        position={index}
        variableType={variable.type}
        domain={variable.domain}
        type={
          type === 'experiment'
            ? ConstraintsViewerType.EXPERIMENT
            : ConstraintsViewerType.ROLLOUT
        }
        targetValueRenderer={
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            {items}
          </div>
        }
      />
      <Button
        type="secondary"
        size="small"
        target="_blank"
        href={appendEnvParam(href)}
        style={{
          justifyContent: 'center',
        }}
      >
        {`Click to view ${type}`}
      </Button>
    </div>
  );
};

const VariablesSearchRollout: FC<VariablesSearchRolloutProps> = ({
  variable,
  matchingRolloutTarget,
  renderAll = false,
  colIndex,
  columns,
  searchVal,
  filters,
  pageNumber,
  total,
}) => {
  const debounceMouseEnter = debounce((e) => {
    e.stopPropagation();

    sendScribeEvent(searchByConstraintsEvents.hoverRolloutTarget.name, {
      STATE_NAME: searchByConstraintsEvents.hoverRolloutTarget.state,
      ...generateScribeEventData(
        colIndex,
        columns,
        filters,
        pageNumber,
        total,
        searchVal,
      ),
    });
  }, 500);

  const renderConstraintIcon = (
    variable,
    rolloutTarget: RolloutTarget,
    index: number,
  ) => {
    return (
      <Popover
        color="#FFF"
        content={renderConstraintPopover(
          variable,
          rolloutTarget,
          index,
          ConstraintsViewerType.ROLLOUT,
        )}
        placement="left"
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 8,
          }}
          onMouseEnter={debounceMouseEnter}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <g clipPath="url(#clip0_3868_32853)">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14ZM15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8ZM8 5.75C8.41421 5.75 8.75 5.41421 8.75 5C8.75 4.58579 8.41421 4.25 8 4.25C7.58579 4.25 7.25 4.58579 7.25 5C7.25 5.41421 7.58579 5.75 8 5.75ZM8.55001 6.5H7.45001L7.45001 11.5H8.55001V6.5Z"
                fill="#1A1A1A"
              />
            </g>
            <defs>
              <clipPath id="clip0_3868_32853">
                <rect width="16" height="16" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
      </Popover>
    );
  };

  const renderValue = (rolloutTarget: RolloutTarget) => {
    switch (variable?.type) {
      case 'boolean':
        return rolloutTarget.value ? 'true' : 'false';
      case 'int64':
      case 'float64':
        return rolloutTarget?.value;
      default:
        return rolloutTarget?.string;
    }
  };

  const renderRollout = (
    variable,
    rolloutTarget: RolloutTarget,
    index: number,
    maxRolloutTargetPosition: number,
  ) => {
    return (
      <div
        key={`variables-search-rollout-${index}`}
        className="flex-row-center-aligned w100 open-object-rollout"
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onClick={(_e) => {
          sendScribeEvent(searchByConstraintsEvents.clickRolloutTarget.name, {
            STATE_NAME: searchByConstraintsEvents.clickRolloutTarget.state,
            ...generateScribeEventData(
              colIndex,
              columns,
              filters,
              pageNumber,
              total,
              searchVal,
            ),
          });

          window.open(
            getUrlBasedOffOriginAndDomainFamily(
              `/variables/${variable.name}/rollout?highlightTargetId=${index}`,
            ),
            '_blank',
          );
        }}
      >
        <TypeIDWrapper valuelength={String(maxRolloutTargetPosition).length}>
          {index}
        </TypeIDWrapper>
        <ValueWrapper
          style={{
            display: 'flex',
            alignItems: 'center',
            width: `calc(100% - ${
              String(maxRolloutTargetPosition).length === 4 ? '48px' : '32px'
            })`,
          }}
        >
          <div
            style={{
              padding: '8px 8px 8px 8px',
              width: 'calc(100% - 32px)',
            }}
          >
            <Text ellipsis showTooltip={false}>
              {renderValue(rolloutTarget)}
            </Text>
          </div>
          {renderConstraintIcon(variable, rolloutTarget, index)}
        </ValueWrapper>
      </div>
    );
  };

  const renderRolloutTargets = (
    variable,
    rolloutTargets: Map<number, RolloutTarget>,
  ) => {
    const rolloutTargetsCount = Array.from(rolloutTargets?.keys()).length || 0;

    if (!rolloutTargetsCount) return [];

    const items = [];
    const lasKey: number = Array.from(rolloutTargets.keys()).pop();
    const maxRolloutTargetPosition = lasKey || 0;
    let countTracker = 0;

    rolloutTargets.forEach((rolloutTarget: RolloutTarget, index: number) => {
      if (countTracker === rolloutTargetsCount - 1 && !renderAll) return;

      items.push(
        renderRollout(variable, rolloutTarget, index, maxRolloutTargetPosition),
      );

      countTracker += 1;
    });

    return items;
  };

  const rolloutsRenderer = () => {
    const rollout = variable?.rollouts;
    const rolloutDefault = rollout?.default;

    const rolloutTargets = renderRolloutTargets(
      variable,
      matchingRolloutTarget,
    );

    if (!rolloutTargets?.length && rolloutDefault) {
      return (
        <div
          key="rollout-default"
          className="flex-row-center-aligned half-gap w100"
        >
          <Tooltip placement="left" title="Default" zIndex={1}>
            <div
              style={{
                display: 'flex',
                width: '32px',
                minWidth: '32px',
                height: '32px',
                backgroundColor: 'rgba(255, 132, 4, 0.10)',
                borderRadius: '8px',
                justifyContent: 'center',
                alignItems: 'center',
                alignSelf: 'stretch',
              }}
            >
              <DefaultValueIcon />
            </div>
          </Tooltip>

          <div
            className="open-object-default"
            style={{
              display: 'flex',
              width: 'calc(100% - 40px)',
              height: '32px',
              borderRadius: '8px',
              alignItems: 'center',
              alignSelf: 'stretch',
              border: '1px solid #E8E8E8',
              padding: '8px 0 8px 8px',
            }}
          >
            <div
              className="flex-row-center-aligned w100"
              onClick={(_e) => {
                window.open(
                  getUrlBasedOffOriginAndDomainFamily(
                    `/variables/${variable.name}/rollout?highlightTargetId=0`,
                  ),
                  '_blank',
                );
              }}
            >
              <div
                style={{
                  padding: '8px 8px 8px 8px',
                  width: 'calc(100% - 32px)',
                }}
              >
                <Text ellipsis showTooltip={false}>
                  {renderValue(rolloutDefault)}
                </Text>
              </div>
              {renderConstraintIcon(variable, rolloutDefault, -1)}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key="rollout-targets"
        className="flex-row-center-aligned half-gap w100"
      >
        <Tooltip title="Rollout" placement="left" zIndex={1}>
          <div
            style={{
              display: 'flex',
              width: '32px',
              minWidth: '32px',
              backgroundColor: 'rgba(0, 177, 79, 0.10)',
              borderRadius: '8px',
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'stretch',
            }}
          >
            <RolloutIcon />
          </div>
        </Tooltip>

        <div
          className="flex-col half-gap"
          style={{
            width: `calc(100% - 40px)`,
          }}
        >
          {rolloutTargets}
        </div>
      </div>
    );
  };

  return rolloutsRenderer();
};

export default VariablesSearchRollout;
